import { Location } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { HeaderService } from 'core';
import { CustomerConfigurationService, CustomerConfigurationState } from '../../services/customer-configuration.service';

@Component({
  selector: 'ord-configure-customer-gdpr',
  imports: [MatButtonModule, MatCardModule, RouterLink, TranslocoModule],
  providers: [provideTranslocoScope('orders')],
  templateUrl: './configure-customer-gdpr.html',
  styleUrl: './configure-customer-gdpr.scss',
})
export class ConfigureCustomerGdpr implements OnInit, OnDestroy {
  readonly configuration = inject(CustomerConfigurationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly headerService = inject(HeaderService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  readonly proposalId = this.route.snapshot.queryParamMap.get('id');
  readonly customer = computed(() => this.configuration.proposalId() === this.proposalId ? this.configuration.customer() : null);
  readonly dragging = signal(false);
  readonly fileError = signal<'bankingFileTypeError' | 'bankingFileSizeError' | 'bankingSingleFileError' | null>(null);
  readonly maxFileBytes = 10 * 1024 * 1024;
  readonly canContinue = computed(() => !!this.customer() && !!this.configuration.destinationForConfiguration()
    && !!this.configuration.gdprDocument() && !this.fileError());

  ngOnInit(): void {
    this.headerService.removeActions();
    this.transloco.selectTranslate<string>('newCustomer.title', {}, 'orders')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(title => this.headerService.setTitle(title));
    this.configuration.restoreFlowState(this.location.getState() as Partial<CustomerConfigurationState> | null, this.proposalId);
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
    this.headerService.removeActions();
  }

  chooseFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.attachFile(file);
    }
    input.value = '';
  }

  dragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(true);
  }

  dropFile(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(false);
    const files = event.dataTransfer?.files;
    if (!files?.length) {
      return;
    }
    if (files.length !== 1) {
      this.fileError.set('bankingSingleFileError');
      return;
    }
    this.attachFile(files[0]);
  }

  removeFile(): void {
    this.configuration.gdprDocument.set(null);
    this.fileError.set(null);
  }

  continue(): void {
    if (!this.canContinue()) {
      return;
    }
    this.configuration.gdprDeferred.set(false);
    this.advanceConfiguration();
  }

  skipForNow(): void {
    if (!this.customer() || !this.configuration.destinationForConfiguration()) {
      return;
    }
    // Il passaggio è rimandato: non implica che il consenso sia stato acquisito.
    this.configuration.gdprDeferred.set(true);
    this.advanceConfiguration();
  }

  private attachFile(file: File): void {
    if (!/\.(pdf|jpe?g|png)$/i.test(file.name) || (file.type && !['application/pdf', 'image/jpeg', 'image/png'].includes(file.type))) {
      this.fileError.set('bankingFileTypeError');
      return;
    }
    if (file.size > this.maxFileBytes) {
      this.fileError.set('bankingFileSizeError');
      return;
    }
    this.fileError.set(null);
    // Solo allegato locale nella bozza: nessun upload o modifica del consenso sull'anagrafica.
    this.configuration.gdprDocument.set(file);
  }

  private advanceConfiguration(): void {
    // TODO: Integrare le future API di creazione cliente e caricamento del consenso firmato.
    // Per ora entrambi i pulsanti tornano alla proposta senza assegnare il cliente configurato.
    void this.router.navigate(['/new-order-proposal'], {
      queryParams: { id: this.proposalId },
      replaceUrl: true,
    });
  }
}
